import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  UsersIcon,
  ShoppingCartIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { MetaLogo, GoogleAdsLogo } from '../icons';

function MarketingDashboard() {
  const [selectedTimeframe, setSelectedTimeframe] = useState('7d');
  const [selectedPlatform, setSelectedPlatform] = useState('all');

  const metrics = [
    {
      name: 'Ad Spend',
      value: '$12,345',
      change: '+8%',
      changeType: 'increase',
      icon: CurrencyDollarIcon
    },
    {
      name: 'ROAS',
      value: '3.2x',
      change: '+0.4',
      changeType: 'increase',
      icon: ChartBarIcon
    },
    {
      name: 'CTR',
      value: '2.8%',
      change: '+0.3%',
      changeType: 'increase',
      icon: UsersIcon
    },
    {
      name: 'Conversions',
      value: '456',
      change: '+12%',
      changeType: 'increase',
      icon: ShoppingCartIcon
    }
  ];

  const campaigns = [
    {
      id: 'CAM-001',
      name: 'Early Black Friday',
      platform: 'meta',
      status: 'Active',
      spend: 4500,
      impressions: '125K',
      clicks: '2.8K',
      conversions: 145
    },
    {
      id: 'CAM-002',
      name: 'Product Launch',
      platform: 'google-ads',
      status: 'Active',
      spend: 3200,
      impressions: '98K',
      clicks: '1.9K',
      conversions: 89
    },
    {
      id: 'CAM-003',
      name: 'Retargeting',
      platform: 'meta',
      status: 'Paused',
      spend: 1800,
      impressions: '45K',
      clicks: '950',
      conversions: 42
    }
  ];

  const alerts = [
    {
      type: 'warning',
      message: 'CAM-001 budget is 80% spent',
      action: 'Review'
    },
    {
      type: 'info',
      message: 'New audience segment ready for targeting',
      action: 'View'
    },
    {
      type: 'warning',
      message: 'CTR below benchmark for CAM-002',
      action: 'Optimize'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-800',
      'Paused': 'bg-yellow-100 text-yellow-800',
      'Ended': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'meta':
        return <MetaLogo className="h-5 w-5" />;
      case 'google-ads':
        return <GoogleAdsLogo className="h-5 w-5" />;
      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Marketing Overview</h1>
        <p className="mt-1 text-sm text-gray-500">
          Monitor your marketing campaigns and performance
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 flex space-x-4">
        <select
          value={selectedTimeframe}
          onChange={(e) => setSelectedTimeframe(e.target.value)}
          className="rounded-lg border-gray-300 text-sm"
        >
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
        </select>

        <select
          value={selectedPlatform}
          onChange={(e) => setSelectedPlatform(e.target.value)}
          className="rounded-lg border-gray-300 text-sm"
        >
          <option value="all">All Platforms</option>
          <option value="meta">Meta Ads</option>
          <option value="google">Google Ads</option>
        </select>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {metrics.map((metric) => (
          <div
            key={metric.name}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 bg-[#0049ac]/10 rounded-lg">
                  <metric.icon className="h-6 w-6 text-[#0049ac]" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">{metric.name}</p>
                  <p className="text-2xl font-semibold text-gray-900">{metric.value}</p>
                </div>
              </div>
              {metric.change && (
                <div className={`flex items-center ${
                  metric.changeType === 'increase' ? 'text-green-600' : 
                  metric.changeType === 'decrease' ? 'text-red-600' : 
                  'text-gray-600'
                }`}>
                  <svg
                    className={`h-4 w-4 mr-1 ${
                      metric.changeType === 'decrease' ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                  <span className="text-sm font-medium">{metric.change}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Active Campaigns */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Active Campaigns</h2>
                <Link
                  to="/strategy/campaign/new"
                  className="text-sm font-medium text-[#0049ac] hover:text-[#0049ac]/90"
                >
                  Create Campaign
                </Link>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Spend</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Impressions</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Clicks</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Conv.</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {campaign.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPlatformIcon(campaign.platform)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(campaign.status)}`}>
                          {campaign.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        ${campaign.spend.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {campaign.impressions}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {campaign.clicks}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {campaign.conversions}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div>
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Alerts</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {alerts.map((alert, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50"
                  >
                    <div className="flex-shrink-0">
                      {alert.type === 'warning' ? (
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                      ) : (
                        <CheckCircleIcon className="h-5 w-5 text-blue-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{alert.message}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <button className="text-sm font-medium text-[#0049ac] hover:text-[#0049ac]/90">
                        {alert.action}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MarketingDashboard;